"use client";

import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Conversation } from "@/hooks/use-webrtc";

export type ConversationLogPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  conversation: Conversation[];
  agentName: string;
};

export default function ConversationLogPanel({
  isOpen,
  onClose,
  conversation,
  agentName,
}: ConversationLogPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isOpen, conversation]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          "fixed top-0 right-0 h-full w-80 bg-white shadow-2xl border-l border-[#e99bad]/10 z-50 flex flex-col transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">対話ログ</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {conversation.map((msg, i) => (
            <div key={i}>
              <span
                className={cn(
                  "text-[11px] font-medium mb-1 block",
                  msg.role === "assistant"
                    ? "text-[#e99bad]"
                    : "text-slate-400"
                )}
              >
                {msg.role === "assistant" ? agentName : "User"}
              </span>
              <div
                className={cn(
                  "rounded-lg px-3 py-2 text-sm",
                  msg.role === "assistant"
                    ? "bg-[#e99bad]/10 text-slate-700"
                    : "bg-slate-100 text-slate-700"
                )}
              >
                {msg.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
